import { Injectable } from '@angular/core';
import {
  MenuParam,
  RecordListResponse,
  DisplaySetting,
  RecordListType,
  KirokuMenu,
} from '@app/models/record.model';
import { Action, State, StateContext } from '@ngxs/store';
import { KirokuSetting } from '@app/models/record-settig.model';
import { CareConneSenderByStudent } from '@app/models/note.model';
import * as lodash from 'lodash';
import { CareConneSenderCreate, CareConneSenderCreateByStudent, CareConneSenderEmpty, CareConneSendersCreateByStudent, CleanCareConneSendersCreateByStudent, ClearListEnvs, ClearRecordList, GetInitFacesheetList, RemoveInitFacesheetList, SetDisplaySetting, SetInitialRecordListSetting, SetKirokuSetting, SetListEnv, SetListEnvXML, SetSetting } from './record.classes';

const defaultSetting = {
  daicd: [],
  staffCd: [],
  sdate: null,
  edate: null,
  keyword: [],
  pickupflg: 0,
  lifeflg: 0,
  title: [],
};

export interface RecordStateModel {
  setting: MenuParam & DisplaySetting & RecordListType;
  settingList: (MenuParam & DisplaySetting)[];
  recordListResponse: RecordListResponse | null;
  facesheetResponse: RecordListResponse | null;
  filterSdate: Date | null;
  filterEdate: Date | null;
  newLoad: boolean;
  waitingLoad: boolean;
  LastLoadId: string;
  pending: boolean;
  listEnvXML: any;
  listEnv: object | null;
  kirokuSetting: KirokuSetting;
  careconneSendObject?: { roomIds: number[], message: string, isMessageOptional?: boolean };
  careconneSendObjectsByStudent: CareConneSenderByStudent[];
}

@State<RecordStateModel>({
  name: 'record',
  defaults: {
    kirokuSetting: {
      daicds: [],
      ttl: [],
      sdate: '',
      edate: '',
      pickupflg: false,
      isInit: true,
      screen: KirokuMenu.EMPTY,
      lastNoteValue: ''
    },
    setting: {
      ckbnId: null,
      id: null,
      ...defaultSetting,
      offsetPaging: null
    },
    settingList: [],
    recordListResponse: null,
    facesheetResponse: null,
    filterSdate: null,
    filterEdate: null,
    pending: false,
    newLoad: false,
    waitingLoad: false,
    LastLoadId: '',
    listEnvXML: null,
    listEnv: null,
    careconneSendObjectsByStudent: []
  },
})
@Injectable()
export class RecordState {
  constructor() { }

  @Action(SetSetting)
  setSetting(
    ctx: StateContext<RecordStateModel>,
    { menuParam, daicd }: SetSetting
  ) {
    const { settingList } = ctx.getState();
    const { ckbnId, id } = menuParam;
    const _setting = settingList.find(
      (item) => item.ckbnId === ckbnId && item.id === id
    );

    const setting = _setting ?? { ...menuParam, ...defaultSetting };
    const personal = daicd ? { daicd: [daicd] } : null;

    ctx.patchState({
      setting: { ...setting, ...personal, offsetPaging: null },
    });
  }

  @Action(GetInitFacesheetList)
  setInitRecords(
    ctx: StateContext<RecordStateModel>,
    { recordList }: GetInitFacesheetList
  ) {
    ctx.patchState({
      facesheetResponse: { record: [recordList], displaySdate: null, displayEdate: null }
    });
  }
  @Action(RemoveInitFacesheetList)
  removeInitRecords(
    ctx: StateContext<RecordStateModel>,
    { }: GetInitFacesheetList
  ) {
    ctx.patchState({
      facesheetResponse: null
    });
  }

  @Action(SetDisplaySetting)
  setDisplaySetting(
    ctx: StateContext<RecordStateModel>,
    { displaySetting }: SetDisplaySetting
  ) {
    const { setting, settingList } = ctx.getState();
    const { ckbnId, id } = setting;
    const hasMenu = settingList.some(
      (item) => item.ckbnId === ckbnId && item.id === id
    );
    const list = hasMenu
      ? settingList.map((item) =>
        item.ckbnId === ckbnId && item.id === id
          ? { ...item, ...displaySetting }
          : item
      )
      : [...settingList, { ...setting, ...displaySetting }];
    ctx.patchState({
      setting: { ...setting, ...displaySetting },
      settingList: list,
    });
  }
  @Action(ClearRecordList)
  clearRecordData(
    ctx: StateContext<RecordStateModel>,
  ) {
    ctx.patchState({
      recordListResponse: null
    })
  }

  @Action(SetInitialRecordListSetting)
  setInitialRecordListSetting(
    ctx: StateContext<RecordStateModel>,
    { payload, daicd }: SetInitialRecordListSetting
  ) {
    const { setting } = ctx.getState();
    const personal = daicd ? { daicd: [daicd] } : null;
    ctx.patchState({ setting: { ...setting, ...payload, ...personal } });
  }

  @Action(SetListEnv)
  setListEnv(
    ctx: StateContext<RecordStateModel>,
    { payload }: SetListEnv
  ) {
    ctx.patchState({ listEnv: payload });
  }

  @Action(SetListEnvXML)
  setListEnvXML(
    ctx: StateContext<RecordStateModel>,
    { payload }: SetListEnvXML
  ) {
    ctx.patchState({ listEnvXML: payload });
  }

  @Action(ClearListEnvs)
  clearListEnvs(
    ctx: StateContext<RecordStateModel>,
  ) {
    ctx.patchState({ listEnv: null });
    ctx.patchState({ listEnvXML: null });
  }

  @Action(SetKirokuSetting)
  setkirokuSetting(
    ctx: StateContext<RecordStateModel>,
    { setting, origin }: SetKirokuSetting
  ) {
    if (!setting) {
      ctx.patchState({ kirokuSetting: undefined })
    } else {
      ctx.patchState({ kirokuSetting: { ...setting, fromOrigin: origin || undefined } })
    }
  }

  @Action(CareConneSenderCreate)
  async careconneSenderCreate(
    ctx: StateContext<RecordStateModel>,
    { roomIds, message, isMessageOptional }: CareConneSenderCreate,
  ) {

    ctx.patchState({
      careconneSendObject: { roomIds, message, isMessageOptional}
    })
  }

  @Action(CareConneSenderEmpty)
  async careconneSenderDestroy(ctx: StateContext<RecordStateModel>) {
    ctx.patchState({
      careconneSendObject: undefined
    })
  }

  @Action(CareConneSenderCreateByStudent)
  async careconneSenderCreateByStudent(
    ctx: StateContext<RecordStateModel>,
    { roomIds, message, daicd, isMessageOptional }: CareConneSenderCreateByStudent,
  ) {
    const { careconneSendObjectsByStudent } = ctx.getState();
    let currentSenders = lodash.cloneDeep(careconneSendObjectsByStudent) ?? [];
    let newSender = { roomIds, message, daicd, isMessageOptional };
    const found = currentSenders.find((s) => s.daicd === daicd);
    if (!found) {
      currentSenders.push(newSender);
    }

    currentSenders = [...currentSenders.filter((s) => s.daicd !== daicd), newSender];
    ctx.patchState({
      careconneSendObjectsByStudent: currentSenders
    });
  }

  @Action(CareConneSendersCreateByStudent)
  careconneSenderCreateMultiple(
    ctx: StateContext<RecordStateModel>,
    { senders }: CareConneSendersCreateByStudent
  ) {
    const { careconneSendObjectsByStudent } = ctx.getState();

    const map = new Map(
      (careconneSendObjectsByStudent ?? []).map(x => [x.daicd, x])
    );

    senders.forEach((item) => {
      if (!item.roomIds.length) {
        map.delete(item.daicd);
      } else {
        map.set(item.daicd, item);
      }
    });

    ctx.patchState({
      careconneSendObjectsByStudent: Array.from(map.values())
    });
  }

  @Action(CleanCareConneSendersCreateByStudent)
  cleanCareConneSendersCreateByStudent(
    ctx: StateContext<RecordStateModel>,
  ) {
    ctx.patchState({ careconneSendObjectsByStudent: [] });
  }
}
